import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Facebook, Twitter, Linkedin, Instagram, Music, Youtube, Palette } from "lucide-react";
import { useSocialMediaLinks } from "@/hooks/useSocialMediaLinks";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { data: socialLinks } = useSocialMediaLinks();

  const socialPlatforms = [
    { url: socialLinks?.facebook_url, icon: Facebook, label: "Facebook", ariaLabel: "Visit our Facebook page" },
    { url: socialLinks?.twitter_url, icon: Twitter, label: "X (Twitter)", ariaLabel: "Visit our X profile" },
    { url: socialLinks?.linkedin_url, icon: Linkedin, label: "LinkedIn", ariaLabel: "Visit our LinkedIn page" },
    { url: socialLinks?.instagram_url, icon: Instagram, label: "Instagram", ariaLabel: "Visit our Instagram profile" },
    { url: socialLinks?.tiktok_url, icon: Music, label: "TikTok", ariaLabel: "Visit our TikTok profile" },
    { url: socialLinks?.youtube_url, icon: Youtube, label: "YouTube", ariaLabel: "Visit our YouTube channel" },
    { url: socialLinks?.pinterest_url, icon: Palette, label: "Pinterest", ariaLabel: "Visit our Pinterest profile" },
  ].filter(platform => platform.url);

  const hasSocialLinks = socialPlatforms.length > 0;

  return (
    <footer className="bg-slate-900 text-slate-300 mt-20">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">iQ Texting</h3>
            <p className="text-sm mb-4">
              AI-powered SMS communication platform for modern businesses.
            </p>
            <p className="text-xs text-slate-400">
              © {currentYear} iQ Texting. All rights reserved.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm hover:text-white transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <a href="/#features" className="text-sm hover:text-white transition-colors">
                  Features
                </a>
              </li>
              <li>
                <a href="/#pricing" className="text-sm hover:text-white transition-colors">
                  Pricing
                </a>
              </li>
              <li>
                <Link to="/dashboard" className="text-sm hover:text-white transition-colors">
                  Dashboard
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal & Resources */}
          <div>
            <h4 className="text-white font-semibold mb-4">Resources</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/blog" className="text-sm hover:text-white transition-colors">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-sm hover:text-white transition-colors">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm hover:text-white transition-colors">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="text-sm hover:text-white transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms-of-use" className="text-sm hover:text-white transition-colors">
                  Terms Of Use
                </Link>
              </li>
              <li>
                <Link to="/accessibility-statement" className="text-sm hover:text-white transition-colors">
                  Accessibility Statement
                </Link>
              </li>
              <li>
                <Link to="/cookie-policy" className="text-sm hover:text-white transition-colors">
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link to="/do-not-sell" className="text-sm hover:text-white transition-colors">
                  Do Not Sell My Info
                </Link>
              </li>
              <li>
                <Link to="/dmca" className="text-sm hover:text-white transition-colors">
                  DMCA
                </Link>
              </li>
            </ul>
          </div>

          {/* User Access */}
          <div>
            <h4 className="text-white font-semibold mb-4">Get Started</h4>
            <p className="text-sm mb-4">
              Access your account or create a new one.
            </p>
            <Button asChild className="w-full md:w-auto">
              <Link to="/auth">User Login</Link>
            </Button>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-8 pt-8">
          {hasSocialLinks && (
            <div className="flex justify-center items-center gap-6 mb-6">
              {socialPlatforms.map(({ url, icon: Icon, label, ariaLabel }) => (
                <a
                  key={label}
                  href={url!}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={ariaLabel}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          )}
          <p className="text-center text-sm text-slate-400">
            Built with care to help you get more customers!
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
